import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState, PrimaryButton, GhostButton, Modal, Input, Select, Table } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Building2, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", type: "school", city: "", state: "", contact_name: "", contact_phone: "", contact_email: "", address: "" };

export default function Institutions() {
  const { hasRole } = useAuth();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const canVerify = hasRole("ops", "admin");
  const load = () => api.get("/institutions").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/institutions", form);
      toast.success("Institution registered - pending verification");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const verify = async (id, status) => {
    try { await api.post(`/institutions/${id}/verify`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!list) return <Spinner />;

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Institutions" subtitle="Schools, colleges and universities"
        action={<PrimaryButton data-testid="add-institution-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Register institution</PrimaryButton>} />

      {list.length === 0 ? (
        <EmptyState icon={Building2} title="No institutions yet" text="Register an institution to start requesting events and trips."
          action={<PrimaryButton onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Register institution</PrimaryButton>} />
      ) : (
        <Table head={["Name", "Type", "Location", "Contact", "Status", canVerify ? "Actions" : ""].filter(Boolean)}>
          {list.map((i) => (
            <tr key={i.id} data-testid={`institution-row-${i.id}`} className="border-b border-border/60 last:border-0 hover:bg-background/40">
              <td className="px-4 py-3 font-medium">{i.name}</td>
              <td className="px-4 py-3 capitalize text-muted-foreground">{i.type}</td>
              <td className="px-4 py-3 text-muted-foreground">{i.city}, {i.state}</td>
              <td className="px-4 py-3 text-muted-foreground">{i.contact_name}<br /><span className="font-mono text-xs">{i.contact_phone}</span></td>
              <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
              {canVerify && (
                <td className="px-4 py-3">
                  {i.status !== "verified" && (
                    <div className="flex gap-2">
                      <button data-testid={`verify-institution-${i.id}`} onClick={() => verify(i.id, "verified")} className="rounded-lg bg-emerald-500/15 p-1.5 text-emerald-300 hover:bg-emerald-500/25"><Check className="h-4 w-4" /></button>
                      <button onClick={() => verify(i.id, "rejected")} className="rounded-lg bg-rose-500/15 p-1.5 text-rose-300 hover:bg-rose-500/25"><X className="h-4 w-4" /></button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </Table>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Register institution" wide>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <Input label="Institution name" testId="inst-name" required value={form.name} onChange={set("name")} className="sm:col-span-2" />
          <Select label="Type" testId="inst-type" value={form.type} onChange={set("type")}>
            <option value="school">School</option><option value="college">College</option><option value="university">University</option>
          </Select>
          <Input label="City" testId="inst-city" required value={form.city} onChange={set("city")} />
          <Input label="State" testId="inst-state" required value={form.state} onChange={set("state")} />
          <Input label="Contact name" testId="inst-contact-name" required value={form.contact_name} onChange={set("contact_name")} />
          <Input label="Contact phone" testId="inst-contact-phone" required value={form.contact_phone} onChange={set("contact_phone")} />
          <Input label="Contact email" testId="inst-contact-email" type="email" required value={form.contact_email} onChange={set("contact_email")} />
          <Input label="Address" testId="inst-address" value={form.address} onChange={set("address")} className="sm:col-span-2" />
          <div className="flex justify-end gap-2 sm:col-span-2">
            <GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton>
            <PrimaryButton data-testid="save-institution-btn" type="submit" disabled={saving}>Register</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
